import { yupResolver } from '@hookform/resolvers/yup'
import { Button, CircularProgress, DialogActions, Divider, Grid, Typography } from '@mui/material'
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useEffect, useState } from 'react'

// Components
import { CustomModal } from '../../../common/CustomModal'
import { ConfirmationModal } from '../../../common/ConfirmationModal'
import { CustomController } from '../../../common/CustomController'
import { CustomDatePickerController } from '../../../common/CustomDatePickerRController'

// Others
import { createManualJournalEntrySchema } from '../../../../validationSchema'
import { CreateManualJournalEntryFormValues } from '../../../../interfaceTypes/forms'
import { Alert } from '../../../common/Alert'
import {
  CreateManualJournalEntryInput,
  JournalEntryType,
  useCreateManualJournalEntriesMutation,
  useFetchJournalEntryLazyQuery
} from '../../../../../generated'
import { CANCEL, GRAPHQL_QUERY_POLICY } from '../../../../constants'
import { JournalEntryLineItemForm } from './JournalEntryLineItemForm'
import { formatCurrency, formatToTwoDecimals, generateUniqueId } from '../../../../utils'
import CheckboxController from '../../../common/CheckboxController'
import InfoTooltip from '../../../common/InfoTooltip'

interface AddJournalEntryModalProps {
  isOpen: boolean
  journalEntryId?: string
  handleClose: () => void
  refetch: () => void
}

const generateEmptyLineItem = () => ({
  id: generateUniqueId(),
  account: null,
  office: null,
  debitAmount: '' as const,
  creditAmount: '' as const
})

const defaultValues: CreateManualJournalEntryFormValues = {
  date: new Date(),
  description: '',
  isClosedYearAdjustment: false,
  lineItems: [generateEmptyLineItem(), generateEmptyLineItem()]
}

const AddJournalEntryModal = ({ isOpen, journalEntryId, handleClose, refetch }: AddJournalEntryModalProps) => {
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)

  const methods = useForm<CreateManualJournalEntryFormValues>({
    mode: 'onChange',
    resolver: yupResolver(createManualJournalEntrySchema),
    defaultValues
  })

  const {
    control,
    reset,
    watch,
    formState: { submitCount, isDirty }
  } = methods
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'lineItems'
  })

  const lineItems = useWatch({
    control,
    name: 'lineItems'
  })
  const totalDebit = lineItems.reduce((sum, item) => sum + (Number(item.debitAmount) || 0), 0)
  const totalCredit = lineItems.reduce((sum, item) => sum + (Number(item.creditAmount) || 0), 0)
  const isBalanced = formatToTwoDecimals(totalDebit) === formatToTwoDecimals(totalCredit)

  const isClosedYearAdjustment = watch('isClosedYearAdjustment');

  const [createManualJournalEntries, { loading }] = useCreateManualJournalEntriesMutation({
    onError(error) {
      Alert.error(error.message)
    },
    onCompleted(data) {
      const { createManualJournalEntries } = data || {}
      const { response } = createManualJournalEntries || {}
      const { status } = response || {}

      if (status === 201) {
        Alert.success('Journal entry created successfully!')
        handleClose()
        reset()
        refetch()
      }
    }
  })

  const [fetchJournalEntry] = useFetchJournalEntryLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError(error) {
      console.error('Error fetching journal entry', error.message)
      Alert.error('Error fetching journal entry')
    },
    onCompleted(data) {
      const journalEntry = data?.fetchJournalEntry || {}

      if (journalEntry) {
        const { date, notes, lineItems: fetchedLineItems } = journalEntry

        reset({
          date: new Date(date),
          description: notes || '',
          lineItems:
            fetchedLineItems?.map(item => ({
              id: item.id,
              account: item.account
                ? {
                    label: `${item.account.sortCode ? `(#${item.account.sortCode}) ` : ''}${item.account.name}`,
                    value: item.account.id
                  }
                : null,
              office: item.office
                ? {
                    label: item.office.name,
                    value: item.office.id
                  }
                : null,
              debitAmount: item.type === JournalEntryType.Debit && item.amount ? item.amount : '',
              creditAmount: item.type === JournalEntryType.Credit && item.amount ? item.amount : '',
              description: ''
            })) || []
        })
      }
    }
  })

  const onSubmit = async (data: CreateManualJournalEntryFormValues) => {
    if (!isBalanced) return

    const lineItemsInput = data.lineItems
      .map(item => {
        // Debit entry
        if (item.debitAmount && Number(item.debitAmount) > 0) {
          return {
            type: JournalEntryType.Debit,
            accountId: item.account?.value || '',
            amount: Number(item.debitAmount),
            officeId: item.office?.value || undefined
          }
        }
        // Credit entry
        if (item.creditAmount && Number(item.creditAmount) > 0) {
          return {
            type: JournalEntryType.Credit,
            accountId: item.account?.value || '',
            amount: Number(item.creditAmount),
            officeId: item.office?.value || undefined
          }
        }
        return null
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)

    const journalEntryInput: CreateManualJournalEntryInput = {
      date: data.date,
      notes: data.description || null,
      lineItems: lineItemsInput,
      isClosedYearAdjustment
    }

    await createManualJournalEntries({
      variables: {
        input: {
          journalEntry: journalEntryInput
        }
      }
    })
  }

  const addLineItem = () => {
    append(generateEmptyLineItem())
  }

  const removeLineItem = (index: number) => {
    if (fields.length > 2) {
      remove(index)
    }
  }

  const handleModalClose = () => {
    handleClose()
    reset()
  }

  const handleCloseWithConfirmation = () => {
    if (isDirty) {
      setShowDiscardConfirm(true)
    } else {
      handleModalClose()
    }
  }

  useEffect(() => {
    if (isOpen && journalEntryId) {
      fetchJournalEntry({ variables: { id: journalEntryId } })
    }
  }, [isOpen, journalEntryId, fetchJournalEntry])

  useEffect(() => {
    if (isClosedYearAdjustment) {
      methods.setValue('date', new Date(new Date().getFullYear() - 1, 11, 31))
    } else {
      methods.setValue('date', new Date())
    }
  }, [isClosedYearAdjustment, methods])

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        handleClose={handleCloseWithConfirmation}
        title="New journal entry"
        maxWidth="lg"
        closeOnlyWithButton
      >
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item md={12} sm={12} xs={12}>
                <CheckboxController label={'Closed-Year Journal Entry'} name="isClosedYearAdjustment" />
                {isClosedYearAdjustment && (
                  <InfoTooltip info="This will create adjusting journal entry to last closed year and modify the closed year balances of selected GL accounts." />
                )}
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <CustomDatePickerController
                  controllerName="date"
                  isDisabled={isClosedYearAdjustment}
                  controllerLabel="Date"
                  sx={{ my: 0 }}
                  format="YYYY-MM-DDTHH:mm:ssZ"
                />
                {isClosedYearAdjustment && (
                  <InfoTooltip info="System will set the date to 12/31 of previous closed year." />
                )}
              </Grid>
              <Grid item xs={12} md={8}>
                <CustomController controllerName="description" controllerLabel="Description" sx={{ my: 0 }} />
              </Grid>
            </Grid>

            {fields.map((item, index) => (
              <JournalEntryLineItemForm
                key={item.id}
                lineItemIndex={index}
                onRemove={() => removeLineItem(index)}
                showRemove={fields.length > 2}
              />
            ))}

            <Button
              variant="text"
              type="button"
              onClick={addLineItem}
              sx={{ mb: 0.5, textTransform: 'none', fontSize: '0.875rem' }}
            >
              + Add line item
            </Button>

            {submitCount > 0 && !isBalanced && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                * Total Debits must be equal to total Credits
              </Typography>
            )}

            <Divider />

            <Grid container spacing={2} sx={{ pt: 2 }}>
              <Grid item xs={0} md={3} />
              <Grid item xs={12} md={3}>
                <Typography sx={{ fontWeight: 600 }}>Total:</Typography>
              </Grid>
              <Grid item xs={12} md={2.5}>
                <Typography sx={{ fontWeight: 600 }}>{formatCurrency(totalDebit)}</Typography>
              </Grid>
              <Grid item xs={12} md={2.5}>
                <Typography sx={{ fontWeight: 600 }}>{formatCurrency(totalCredit)}</Typography>
              </Grid>
            </Grid>

            <DialogActions sx={{ padding: '30px 0 0' }}>
              <Button onClick={handleCloseWithConfirmation} variant="outlined">
                {CANCEL}
              </Button>

              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={loading}
                endIcon={loading && <CircularProgress size={20} color="inherit" />}
              >
                Create journal entry
              </Button>
            </DialogActions>
          </form>
        </FormProvider>
      </CustomModal>

      <ConfirmationModal
        isOpen={showDiscardConfirm}
        title="Discard changes?"
        message="All changes you've made will be lost. Are you sure you want to close?"
        confirmText="Discard"
        cancelText="Keep editing"
        variant="danger"
        onConfirm={() => {
          setShowDiscardConfirm(false)
          handleModalClose()
        }}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </>
  )
}

export default AddJournalEntryModal
